/* eslint-disable react/no-unescaped-entities */
import { useCallback, useEffect, useState } from "react";
import {
  Cloud,
  Copy,
  Link2,
  Download,
  Upload,
  Rss,
  Code,
  Webhook as WebhookIcon,
  Trash2,
  Send,
  Plus,
  Car,
  Facebook,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import api, { API } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import SftpImportCard from "@/components/SftpImportCard";
import { toast } from "sonner";

const SAMPLE_FEED = JSON.stringify(
  {
    listings: [
      {
        external_id: "EXT-001",
        make: "Mazda",
        model: "CX-5",
        year: 2022,
        price: 26900,
        mileage: 23100,
        body_type: "SUV",
        transmission: "Automatic",
        fuel_type: "Gasoline",
        drivetrain: "AWD",
        exterior_color: "Soul Red",
        description: "Imported from partner feed. Beautifully maintained.",
        features: ["Leather", "Sunroof", "Apple CarPlay"],
        image_urls: ["https://images.unsplash.com/photo-1698413935252-04ed6377296d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"],
      },
    ],
  },
  null,
  2,
);

const ALL_EVENTS = ["car.created", "car.updated", "car.sold", "car.deleted", "inquiry.created"];

export default function AdminSync() {
  const [feedUrl, setFeedUrl] = useState("");
  const [sourceName, setSourceName] = useState("Partner Feed");
  const [jsonInput, setJsonInput] = useState(SAMPLE_FEED);
  const [busy, setBusy] = useState(false);

  // Webhooks
  const [hooks, setHooks] = useState([]);
  const [inbound, setInbound] = useState(null);
  const [csvBusy, setCsvBusy] = useState(false);
  const [newHook, setNewHook] = useState({
    url: "",
    label: "",
    events: ["car.created", "car.sold", "inquiry.created"],
  });

  const publicUrl = `${BRAND.publicApi}/public/listings`;
  const rssUrl = `${BRAND.publicApi}/feed.rss`;
  const carsforsaleFeedUrl = `${BRAND.publicApi}/feeds/carsforsale.csv`;
  const facebookFeedUrl = `${BRAND.publicApi}/feeds/facebook.csv`;
  const embedSnippet = `<div id="pit-stop-motors-listings"></div>
<script src="${BRAND.publicApi}/embed.js" data-limit="6" data-columns="3" async></script>`;

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const importFromUrl = async () => {
    if (!feedUrl) {
      toast.error("Enter a feed URL first");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/cars/import", { feed_url: feedUrl, source_name: sourceName });
      toast.success(`Imported ${r.data.inserted}, skipped ${r.data.skipped}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const importFromJson = async () => {
    setBusy(true);
    try {
      const parsed = JSON.parse(jsonInput);
      const cars = Array.isArray(parsed) ? parsed : parsed.listings;
      const r = await api.post("/cars/import", { cars, source_name: sourceName });
      toast.success(`Imported ${r.data.inserted}, skipped ${r.data.skipped}`);
    } catch (err) {
      toast.error(err?.message || "Invalid JSON");
    } finally {
      setBusy(false);
    }
  };

  const loadHooks = useCallback(async () => {
    try {
      const r = await api.get("/webhooks");
      setHooks(r.data || []);
    } catch (e) {
      console.warn("Failed to load webhooks:", e);
    }
  }, []);

  const loadInbound = useCallback(async () => {
    try {
      const r = await api.get("/feeds/inbound/info");
      setInbound(r.data);
    } catch (e) {
      console.warn("Failed to load inbound feed info:", e);
    }
  }, []);

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !inbound) return;
    setCsvBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/feeds/inbound/${inbound.token}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data;
      toast.success(`Feed imported: ${d.inserted} new, ${d.updated} updated, ${d.marked_sold} marked sold${d.skipped ? `, ${d.skipped} skipped` : ""}`);
      loadInbound();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "CSV import failed");
    } finally {
      setCsvBusy(false);
      e.target.value = "";
    }
  };

  useEffect(() => {
    loadHooks();
    loadInbound();
  }, [loadHooks, loadInbound]);

  const addHook = async (e) => {
    e?.preventDefault();
    if (!newHook.url) {
      toast.error("Enter a webhook URL");
      return;
    }
    if (newHook.events.length === 0) {
      toast.error("Pick at least one event");
      return;
    }
    try {
      await api.post("/webhooks", newHook);
      toast.success("Webhook saved");
      setNewHook({ url: "", label: "", events: ["car.created", "car.sold", "inquiry.created"] });
      loadHooks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    }
  };

  const testHook = async (id) => {
    try {
      const r = await api.post(`/webhooks/${id}/test`);
      if (r.data?.ok) {
        toast.success(`Test delivered (HTTP ${r.data.status})`);
      } else {
        toast.error(`Test failed (status ${r.data?.status ?? "0"})`);
      }
      loadHooks();
    } catch (err) {
      toast.error("Test failed");
    }
  };

  const removeHook = async (id) => {
    if (!window.confirm("Remove this webhook subscription?")) return;
    try {
      await api.delete(`/webhooks/${id}`);
      toast.success("Removed");
      loadHooks();
    } catch (err) {
      toast.error("Failed");
    }
  };

  const toggleEvent = (ev) => {
    setNewHook((h) => ({
      ...h,
      events: h.events.includes(ev) ? h.events.filter((e) => e !== ev) : [...h.events, ev],
    }));
  };

  return (
    <div data-testid="admin-sync" className="space-y-10">
      <div>
        <h2 className="font-serif text-3xl text-primary">Sync & Talk to Other Sites</h2>
        <p className="text-sm text-muted-foreground">
          Share your inventory with partners, pull listings from feeds, and get notified about every event.
        </p>
      </div>

      {/* CONNECTED CHANNELS */}
      <section data-testid="connected-channels" className="space-y-4">
        <div>
          <p className="overline">Connected Channels</p>
          <h3 className="mt-2 font-serif text-2xl text-primary">Your inventory, syndicated.</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Two ready-to-use feed URLs for the platforms you've connected to. Send the URL to the platform once, and your inventory stays in sync automatically.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Carsforsale.com */}
          <div className="border-2 border-soft hover:border-accent transition-colors bg-card p-6 md:p-7" data-testid="channel-carsforsale">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <span className="h-10 w-10 inline-flex items-center justify-center bg-primary text-primary-foreground rounded-sm">
                  <Car className="h-4 w-4" strokeWidth={1.5} />
                </span>
                <div>
                  <h4 className="font-serif text-xl text-primary leading-tight">Carsforsale.com</h4>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">CSV inventory feed</p>
                </div>
              </div>
              <a
                href={BRAND.carsforsaleUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-muted-foreground hover:text-accent inline-flex items-center gap-1"
                data-testid="open-carsforsale"
              >
                Visit <ExternalLink className="h-3 w-3" />
              </a>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Input value={carsforsaleFeedUrl} readOnly className="flex-1 font-mono text-xs" data-testid="carsforsale-feed-url" />
              <Button variant="outline" onClick={() => copyText(carsforsaleFeedUrl, "Carsforsale feed URL")} className="rounded-sm gap-1" data-testid="copy-carsforsale">
                <Copy className="h-3 w-3" /> Copy
              </Button>
            </div>
            <div className="mt-4 text-xs text-muted-foreground leading-relaxed bg-secondary/50 border border-soft p-3">
              <p className="font-medium text-primary mb-1.5">How to connect:</p>
              <ol className="space-y-1 list-decimal list-inside">
                <li>Email this URL to your Carsforsale.com rep (or paste into their dealer portal feed settings).</li>
                <li>Carsforsale.com will pull your inventory automatically &mdash; usually every few hours.</li>
                <li>Mark a vehicle <code className="bg-background px-1 rounded-sm">sold</code> here and it drops off Carsforsale automatically.</li>
              </ol>
            </div>
          </div>

          {/* Facebook */}
          <div className="border-2 border-soft hover:border-accent transition-colors bg-card p-6 md:p-7" data-testid="channel-facebook">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <span className="h-10 w-10 inline-flex items-center justify-center bg-primary text-primary-foreground rounded-sm">
                  <Facebook className="h-4 w-4" strokeWidth={1.5} />
                </span>
                <div>
                  <h4 className="font-serif text-xl text-primary leading-tight">Facebook Catalog</h4>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">Commerce Manager · Vehicles</p>
                </div>
              </div>
              <a
                href={BRAND.facebookUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-muted-foreground hover:text-accent inline-flex items-center gap-1"
                data-testid="open-facebook-page"
              >
                Your Page <ExternalLink className="h-3 w-3" />
              </a>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Input value={facebookFeedUrl} readOnly className="flex-1 font-mono text-xs" data-testid="facebook-feed-url" />
              <Button variant="outline" onClick={() => copyText(facebookFeedUrl, "Facebook feed URL")} className="rounded-sm gap-1" data-testid="copy-facebook">
                <Copy className="h-3 w-3" /> Copy
              </Button>
            </div>
            <div className="mt-4 text-xs text-muted-foreground leading-relaxed bg-secondary/50 border border-soft p-3">
              <p className="font-medium text-primary mb-1.5">How to connect:</p>
              <ol className="space-y-1 list-decimal list-inside">
                <li>Open <a href="https://business.facebook.com/commerce" target="_blank" rel="noreferrer" className="text-accent hover:underline">Facebook Commerce Manager</a>.</li>
                <li>Create a Vehicles catalog &rarr; Data Sources &rarr; <strong>Use a data feed URL</strong>.</li>
                <li>Paste this URL. Set schedule to "Daily" or "Hourly" and you're live on Marketplace.</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* INBOUND DMS FEED */}
      <section className="border-2 border-soft bg-card p-6 md:p-8" data-testid="inbound-feed-section">
        <div className="flex items-start gap-3">
          <Download className="h-5 w-5 text-accent mt-1" strokeWidth={1.5} />
          <div className="flex-1">
            <h3 className="font-serif text-2xl text-primary">Receive Inventory From Your DMS</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Give this secure HTTPS drop URL to DealerCenter (or any DMS). When they POST their CSV export here,
              your website inventory updates automatically — new cars added, prices updated, missing cars marked sold.
            </p>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <Input value={inbound?.url || "Loading…"} readOnly className="flex-1 font-mono text-xs" data-testid="inbound-feed-url" />
              <Button variant="outline" onClick={() => copyText(inbound?.url || "", "Inbound feed URL")} className="rounded-sm gap-1" data-testid="copy-inbound-url">
                <Copy className="h-3 w-3" /> Copy
              </Button>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button asChild variant="outline" className="rounded-sm gap-1 cursor-pointer" disabled={csvBusy} data-testid="upload-csv-btn">
                <label>
                  <Upload className="h-3 w-3" /> {csvBusy ? "Importing…" : "Upload a CSV manually"}
                  <input type="file" accept=".csv,.txt,text/csv,text/plain" className="hidden" onChange={uploadCsv} data-testid="upload-csv-input" />
                </label>
              </Button>
              <span className="text-xs text-muted-foreground">Accepts comma, pipe or tab delimited files with a header row.</span>
            </div>
            {inbound?.last_import && (
              <div className="mt-4 text-xs text-muted-foreground bg-secondary/50 border border-soft p-3" data-testid="last-import-summary">
                <span className="font-medium text-primary">Last import ({inbound.last_import.source_name}):</span>{" "}
                {new Date(inbound.last_import.created_at).toLocaleString()} — {inbound.last_import.inserted} new,{" "}
                {inbound.last_import.updated} updated, {inbound.last_import.marked_sold} marked sold, {inbound.last_import.skipped} skipped.
                {inbound.last_import.errors?.length > 0 && (
                  <span className="block mt-1 text-destructive">{inbound.last_import.errors[0]}</span>
                )}
              </div>
            )}
            <p className="mt-3 text-xs text-muted-foreground">
              Required columns: <code className="bg-secondary px-1 rounded-sm">VIN</code>, <code className="bg-secondary px-1 rounded-sm">Make</code>,{" "}
              <code className="bg-secondary px-1 rounded-sm">Model</code>, <code className="bg-secondary px-1 rounded-sm">Year</code>,{" "}
              <code className="bg-secondary px-1 rounded-sm">Price</code>. Optional: Mileage, Trim, Colors, Transmission, Body, Photos (pipe-separated URLs), Description and more.
            </p>
          </div>
        </div>
      </section>

      {/* SFTP PULL IMPORT */}
      <SftpImportCard />

      {/* PUBLIC LISTINGS API + RSS */}
      <section className="grid md:grid-cols-2 gap-4" data-testid="public-endpoints">
        <div className="border border-soft bg-card p-6 md:p-8">
          <div className="flex items-start gap-3">
            <Cloud className="h-5 w-5 text-accent mt-1" strokeWidth={1.5} />
            <div className="flex-1">
              <h3 className="font-serif text-2xl text-primary">Public JSON API</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Live inventory as JSON. CORS-open so any partner can fetch and render it.
              </p>
              <div className="mt-4 flex flex-col sm:flex-row gap-2">
                <Input value={publicUrl} readOnly className="flex-1 font-mono text-xs" data-testid="public-api-url" />
                <Button variant="outline" onClick={() => copyText(publicUrl, "API URL")} className="rounded-sm gap-1" data-testid="copy-api-url">
                  <Copy className="h-3 w-3" /> Copy
                </Button>
                <Button asChild variant="outline" className="rounded-sm gap-1" data-testid="open-api-url">
                  <a href={publicUrl} target="_blank" rel="noreferrer">
                    <Link2 className="h-3 w-3" /> Open
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="border border-soft bg-card p-6 md:p-8">
          <div className="flex items-start gap-3">
            <Rss className="h-5 w-5 text-accent mt-1" strokeWidth={1.5} />
            <div className="flex-1">
              <h3 className="font-serif text-2xl text-primary">RSS Feed</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Standard RSS 2.0 feed of available vehicles. Plug into any aggregator or reader.
              </p>
              <div className="mt-4 flex flex-col sm:flex-row gap-2">
                <Input value={rssUrl} readOnly className="flex-1 font-mono text-xs" data-testid="rss-url" />
                <Button variant="outline" onClick={() => copyText(rssUrl, "RSS URL")} className="rounded-sm gap-1" data-testid="copy-rss-url">
                  <Copy className="h-3 w-3" /> Copy
                </Button>
                <Button asChild variant="outline" className="rounded-sm gap-1" data-testid="open-rss-url">
                  <a href={rssUrl} target="_blank" rel="noreferrer">
                    <Link2 className="h-3 w-3" /> Open
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EMBED SNIPPET */}
      <section className="border border-soft bg-card p-6 md:p-8" data-testid="embed-section">
        <div className="flex items-start gap-3">
          <Code className="h-5 w-5 text-accent mt-1" strokeWidth={1.5} />
          <div className="flex-1">
            <h3 className="font-serif text-2xl text-primary">Embed on Any Partner Website</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Send this snippet to any partner — a church website, a community board, an affiliate site — and your
              live inventory shows up on their page. No login, no API key, no maintenance.
            </p>
            <Textarea
              readOnly
              value={embedSnippet}
              rows={3}
              className="mt-4 font-mono text-xs"
              data-testid="embed-snippet"
            />
            <div className="mt-3 flex flex-wrap gap-2">
              <Button onClick={() => copyText(embedSnippet, "Embed snippet")} className="rounded-sm gap-1" data-testid="copy-embed-snippet">
                <Copy className="h-3 w-3" /> Copy Snippet
              </Button>
              <Button asChild variant="outline" className="rounded-sm gap-1" data-testid="open-embed-js">
                <a href={`${API}/embed.js`} target="_blank" rel="noreferrer">
                  <Link2 className="h-3 w-3" /> View embed.js
                </a>
              </Button>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Tip: pass <code className="bg-secondary px-1 py-0.5 rounded-sm">data-limit="4"</code> or
              <code className="bg-secondary ml-1 px-1 py-0.5 rounded-sm">data-columns="2"</code> on the
              <code className="bg-secondary ml-1 px-1 py-0.5 rounded-sm">&lt;script&gt;</code> tag to control the layout.
              Pass <code className="bg-secondary ml-1 px-1 py-0.5 rounded-sm">data-target="#my-div"</code> to mount into a specific element.
            </p>
          </div>
        </div>
      </section>

      {/* WEBHOOKS */}
      <section className="border border-soft bg-card p-6 md:p-8" data-testid="webhooks-section">
        <div className="flex items-start gap-3">
          <WebhookIcon className="h-5 w-5 text-accent mt-1" strokeWidth={1.5} />
          <div className="flex-1">
            <h3 className="font-serif text-2xl text-primary">Outbound Webhooks</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Notify partner systems in real time when a car is listed, sold, or a customer submits an inquiry.
              We POST a JSON event to every active subscriber.
            </p>

            {/* Existing hooks */}
            <div className="mt-6 space-y-3" data-testid="webhook-list">
              {hooks.length === 0 && (
                <p className="text-sm text-muted-foreground italic">No webhooks subscribed yet.</p>
              )}
              {hooks.map((h) => (
                <div key={h.id} className="border border-soft p-4 bg-background flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`webhook-${h.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-primary text-sm">{h.label || "(no label)"}</span>
                      {h.active && <Badge className="bg-accent text-accent-foreground rounded-sm text-[10px] uppercase tracking-wider">Active</Badge>}
                      {h.last_status != null && (
                        <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-wider">
                          Last: {h.last_status || "error"}
                        </Badge>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground font-mono break-all">{h.url}</p>
                    <p className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                      Events: {h.events.join(" · ")}
                    </p>
                    {h.delivery_count > 0 && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {h.delivery_count} deliveries · {h.failure_count} failures
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => testHook(h.id)} data-testid={`test-webhook-${h.id}`}>
                      <Send className="h-3 w-3 mr-1" /> Test
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => removeHook(h.id)} data-testid={`delete-webhook-${h.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Add new */}
            <form onSubmit={addHook} className="mt-6 border-t border-soft pt-6 space-y-3" data-testid="add-webhook-form">
              <h4 className="font-serif text-lg text-primary flex items-center gap-2"><Plus className="h-4 w-4" /> Add a Webhook</h4>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="wh-url">URL</Label>
                  <Input
                    id="wh-url"
                    placeholder="https://partner.example.com/webhook"
                    value={newHook.url}
                    onChange={(e) => setNewHook({ ...newHook, url: e.target.value })}
                    data-testid="webhook-url-input"
                  />
                </div>
                <div>
                  <Label htmlFor="wh-label">Label (optional)</Label>
                  <Input
                    id="wh-label"
                    placeholder="Zapier · Partner Lot · etc."
                    value={newHook.label}
                    onChange={(e) => setNewHook({ ...newHook, label: e.target.value })}
                    data-testid="webhook-label-input"
                  />
                </div>
              </div>
              <div>
                <Label>Subscribe to events</Label>
                <div className="mt-2 flex flex-wrap gap-3">
                  {ALL_EVENTS.map((ev) => (
                    <label key={ev} className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                      <Checkbox
                        checked={newHook.events.includes(ev)}
                        onCheckedChange={() => toggleEvent(ev)}
                        data-testid={`webhook-event-${ev}`}
                      />
                      <span className="font-mono text-xs">{ev}</span>
                    </label>
                  ))}
                </div>
              </div>
              <Button type="submit" className="rounded-sm gap-1" data-testid="add-webhook-submit">
                <Plus className="h-3 w-3" /> Subscribe
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* IMPORT FROM URL */}
      <section className="border border-soft bg-card p-6 md:p-8" data-testid="import-url-section">
        <div className="flex items-start gap-3">
          <Download className="h-5 w-5 text-accent mt-1" strokeWidth={1.5} />
          <div className="flex-1">
            <h3 className="font-serif text-2xl text-primary">Pull Listings from a Partner Feed</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Import vehicles from another dealer's JSON feed. The feed should return an array, or
              <code className="ml-1 text-xs bg-secondary px-1 py-0.5 rounded-sm">{`{ "listings": [...] }`}</code>.
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label htmlFor="feed-source">Source Name</Label>
                <Input
                  id="feed-source"
                  value={sourceName}
                  onChange={(e) => setSourceName(e.target.value)}
                  data-testid="import-source-name"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="feed-url">Feed URL</Label>
                <Input
                  id="feed-url"
                  placeholder="https://partner.example.com/listings.json"
                  value={feedUrl}
                  onChange={(e) => setFeedUrl(e.target.value)}
                  data-testid="import-feed-url"
                />
              </div>
            </div>
            <Button onClick={importFromUrl} disabled={busy} className="mt-4 rounded-sm gap-1" data-testid="import-url-button">
              <Download className="h-3 w-3" /> {busy ? "Importing…" : "Fetch & Import"}
            </Button>
          </div>
        </div>
      </section>

      {/* PASTE JSON */}
      <section className="border border-soft bg-card p-6 md:p-8" data-testid="import-json-section">
        <div className="flex items-start gap-3">
          <Upload className="h-5 w-5 text-accent mt-1" strokeWidth={1.5} />
          <div className="flex-1">
            <h3 className="font-serif text-2xl text-primary">Paste Listings JSON</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Bulk add vehicles by pasting JSON. Handy for testing or one-time imports.
            </p>
            <Textarea
              className="mt-4 font-mono text-xs"
              rows={10}
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              data-testid="import-json-input"
            />
            <Button onClick={importFromJson} disabled={busy} className="mt-3 rounded-sm gap-1" data-testid="import-json-button">
              <Upload className="h-3 w-3" /> {busy ? "Importing…" : "Import JSON"}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
